<template>
  <section class="ticket-detail-summary">
    <h5 class="summary-title">{{ $t('ticketDetail.sectionReport') }}</h5>

    <div class="summary-ticket">
      <span class="summary-ticket-icon"><CIcon name="cil-tag" size="lg" /></span>
      <span>
        <small>{{ $t('ticketDetail.ticketNo') }}</small>
        <strong>{{ ticketNumber || '-' }}</strong>
      </span>
    </div>

    <div class="summary-grid">
      <div v-for="field in fields" :key="field.key" class="summary-field" :class="field.className">
        <span class="summary-label">{{ field.label }}</span>
        <strong class="summary-value">
          {{ field.value || '-' }}
          <small v-if="field.key === 'reporter' && onBehalfOf" class="summary-note">
            ({{ $t('ticketDetail.onBehalf') }} {{ onBehalfOf }})
          </small>
        </strong>
      </div>
    </div>

    <slot />
  </section>
</template>

<script>
export default {
  name: 'TicketDetailSummary',
  props: {
    ticketNumber: { type: [String, Number], default: '-' },
    reporter: { type: [String, Number], default: '-' },
    affiliation: { type: [String, Number], default: '' },
    section: { type: [String, Number], default: '' },
    department: { type: [String, Number], default: '' },
    personalPhone: { type: [String, Number], default: '-' },
    officePhone: { type: [String, Number], default: '-' },
    jobType: { type: [String, Number], default: '-' },
    workArea: { type: [String, Number], default: '-' },
    timeSlot: { type: [String, Number], default: '-' },
    onBehalfOf: { type: String, default: '' },
    technicianPhone: { type: [String, Number], default: '-' },
  },
  computed: {
    fields() {
      const list = [
        { key: 'reporter', label: this.$t('ticketDetail.reporter'), value: this.reporter },
      ];

      if (this.affiliation) {
        list.push({
          key: 'affiliation',
          label: this.$t('ticketDetail.affiliation'),
          value: this.affiliation,
        });
      }

      if (this.section) {
        list.push({
          key: 'section',
          label: this.$t('ticketDetail.section'),
          value: this.section,
          className: 'summary-field--featured',
        });
      } else if (this.department) {
        list.push({
          key: 'department',
          label: this.$t('ticketDetail.deptCombined'),
          value: this.department,
        });
      }

      list.push(
        { key: 'jobType', label: this.$t('ticketDetail.jobType'), value: this.jobType, className: 'summary-field--featured' },
        { key: 'officePhone', label: this.$t('ticketDetail.phoneWorkShort'), value: this.officePhone },
        { key: 'personalPhone', label: this.$t('ticketDetail.phonePersonalShort'), value: this.personalPhone },
        { key: 'workArea', label: this.$t('ticketDetail.workArea'), value: this.workArea, className: 'summary-field--featured' },
        { key: 'technicianPhone', label: this.$t('ticketDetail.phoneTechnician') || 'เบอร์โทรช่าง:', value: this.technicianPhone, className: 'summary-field--featured' },
        { key: 'timeSlot', label: this.$t('ticketDetail.timeSlot'), value: this.timeSlot, className: 'summary-field--wide summary-field--featured summary-field--timeslot' },
      );

      return list;
    },
  },
}
</script>

<style scoped>
.ticket-detail-summary { margin-bottom: 24px; }
.summary-title { margin: 0 0 14px; padding-left: 12px; border-left: 4px solid #1e40af; color: #1e40af; font-size: 1.15rem; font-weight: 800; }
.summary-ticket { display: flex; min-height: 86px; align-items: center; gap: 14px; padding: 18px 20px; border-radius: 16px; color: #fff; background: #1e40af; box-shadow: 0 12px 28px rgba(37, 99, 235, .16); }
.summary-ticket-icon { display: inline-flex; width: 42px; height: 42px; flex: 0 0 42px; align-items: center; justify-content: center; border-radius: 50%; color: #1e40af; background: #fff; }
.summary-ticket small, .summary-ticket strong { display: block; }
.summary-ticket small { opacity: .8; font-size: .78rem; }
.summary-ticket strong { margin-top: 2px; font-size: 1.45rem; line-height: 1.2; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 14px; padding: 4px 16px; border: 1px solid rgba(37, 99, 235, .11); border-radius: 16px; background: #fff; box-shadow: 0 10px 26px rgba(37, 99, 235, .05); }
.summary-field { min-width: 0; padding: 14px; border-right: 1px solid rgba(37, 99, 235, .08); }
.summary-field:nth-child(4n), .summary-field:last-child { border-right: 0; }
.summary-field--wide { grid-column: span 2; }
.summary-field--featured { border-right-color: rgba(37, 99, 235, .08); background: transparent; }
.summary-field--featured .summary-label { color: #1e40af; font-weight: 800; }
.summary-field--featured .summary-value { color: #1e40af; font-weight: 800; }
.summary-field--timeslot .summary-value { font-size: 0.94rem; line-height: 1.35; white-space: nowrap; }
.summary-label, .summary-value { display: block; }
.summary-label { margin-bottom: 5px; color: #7b6f73; font-size: .78rem; font-weight: 700; }
.summary-value { color: #2f2930; font-size: .98rem; overflow-wrap: anywhere; }
.summary-note { color: #1d4ed8; font-weight: 700; }
@media (max-width: 768px) {
  .summary-grid { grid-template-columns: 1fr; padding: 4px 14px; }
  .summary-field, .summary-field:nth-child(4n) { grid-column: auto; border-right: 0; border-bottom: 1px solid rgba(37, 99, 235, .08); }
  .summary-field--featured { border-bottom: 1px solid rgba(37, 99, 235, .08) !important; }
  .summary-field:last-child { border-bottom: 0; }
  .summary-ticket strong { font-size: 1.25rem; }
  .summary-field--timeslot .summary-value { font-size: 0.88rem; }
}
@media (max-width: 380px) {
  .summary-field--timeslot .summary-value { font-size: 0.82rem; }
}
</style>
